import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState, RootDispatch } from "../../../store";
import {
  createStoreInwardRequests,
  updateStoreInwardRequest,
  fetchRawMaterialsByWarehousePage,
} from "../../../store/slices/storeInwardSlice";
import {
  IWarehouseRawMaterial,
  UNITS,
  UnitType,
} from "../../../models/warehouseRawMaterial.model";
import { toast } from "react-hot-toast";
import Select from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import {
  getDefaultUnitForType,
  getUnitsByType,
} from "../../../utils/warehouse.util";
import {
  fetchRawMaterialsThunk,
  fetchVendorsThunk,
} from "../../../store/slices/warehouseSlice";
import RawMaterialModal from "../Management/RawMaterialModal";
import VendorModal from "../Management/VendorModal";
import { IVendor } from "../../../models/vendor.model";

interface InwardItem {
  productId: string;
  productName: string;
  unitType?: string;
  quantity: string;
  unit: UnitType | "";
  priceType: "unit" | "total" | "";
  unitPrice: string;
  totalPrice: string;
  vendorId: string;
  vendorName?: string;
  warehouseId: string;
  billNumber: string;
  remark: string;
  orderedDate: string;
  receivedDate: string;
}

interface RawMaterialOption {
  value: string;
  label: string;
  unitType: string;
  name: string;
}

interface VendorOption {
  value: string;
  label: string;
}

interface AddStoreInwardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Add this line
  editInwardId?: string | null;
  prefillData?: {
    items: InwardItem[];
    travelCost: string;
    otherCost: string;
  };
  from?: string;
}

const AddStoreInwardModal: React.FC<AddStoreInwardModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editInwardId,
  prefillData,
  from = "factory",
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const location = useLocation();
  const isWarehousePage = location.pathname.toLowerCase().includes("warehouse");
  const { warehouses, loading } = useSelector(
    (state: RootState) => state.storeInward,
  );
  const warehouseAssigned = useSelector(
    (state: RootState) => state.auth.userName?.warehouseAssigned,
  );

  const [rootCosts, setRootCosts] = useState({
    travelCost: "",
    otherCost: "",
  });

  const [items, setItems] = useState<InwardItem[]>([
    {
      productId: "",
      productName: "",
      quantity: "",
      unit: "",
      priceType: "",
      unitPrice: "",
      totalPrice: "",
      vendorId: "",
      warehouseId: warehouseAssigned || "",
      billNumber: "",
      remark: "",
      orderedDate: "",
      receivedDate: "",
    },
  ]);

  const [originalData, setOriginalData] = useState<string>("");
  const [showConfirm, setShowConfirm] = useState(false);
  const rowRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const [isRawMaterialModalOpen, setIsRawMaterialModalOpen] = useState(false);
  const [rawMaterialForm, setRawMaterialForm] = useState<
    Partial<IWarehouseRawMaterial>
  >({});
  // Bumped after creating a material to force the async selects to refetch page 1
  const [materialsRefreshKey, setMaterialsRefreshKey] = useState(0);

  const getRawMaterials = async () => {
    setMaterialsRefreshKey((key) => key + 1);
  };

  const openRawMaterialModal = (name: string) => {
    setRawMaterialForm({ name });
    setIsRawMaterialModalOpen(true);
  };

  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [vendorForm, setVendorForm] = useState<Partial<IVendor>>({});
  // Bumped after creating a vendor to force the async select to refetch page 1
  const [vendorsRefreshKey, setVendorsRefreshKey] = useState(0);

  const openVendorModal = (name: string) => {
    setVendorForm({ name });
    setIsVendorModalOpen(true);
  };

  const loadRawMaterialOptions: LoadOptions<
    RawMaterialOption,
    any,
    { page: number }
  > = async (search, _loadedOptions, additional) => {
    const page = additional?.page || 1;
    try {
      const response = await dispatch(
        fetchRawMaterialsThunk({ page, limit: 10, search, from }),
      ).unwrap();

      return {
        options: response.data.map((rm) => ({
          value: rm._id,
          label: `${rm.name} (${rm.unitType}) - SKU: ${rm.sku}`,
          unitType: rm.unitType,
          name: rm.name,
        })),
        hasMore: page < response.totalPages,
        additional: { page: page + 1 },
      };
    } catch (error) {
      return { options: [], hasMore: false, additional: { page: 1 } };
    }
  };

  const loadVendorOptions: LoadOptions<
    VendorOption,
    any,
    { page: number }
  > = async (search, _loadedOptions, additional) => {
    const page = additional?.page || 1;
    try {
      const response = await dispatch(
        fetchVendorsThunk({ page, limit: 10, search }),
      ).unwrap();

      return {
        options: response.data.map((v) => ({
          value: v._id,
          label: v.name,
        })),
        hasMore: page < response.totalPages,
        additional: { page: page + 1 },
      };
    } catch (error) {
      return { options: [], hasMore: false, additional: { page: 1 } };
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (editInwardId && prefillData) {
        const mappedItems = prefillData.items.map((item) => ({
          ...item,
          priceType: "total" as const,
        }));
        setItems(mappedItems);
        setRootCosts({
          travelCost: prefillData.travelCost,
          otherCost: prefillData.otherCost,
        });
        setOriginalData(
          JSON.stringify({
            items: mappedItems,
            travelCost: prefillData.travelCost,
            otherCost: prefillData.otherCost,
          }),
        );
      } else {
        resetForm();
        setOriginalData("");
      }
    }
  }, [isOpen, editInwardId, prefillData]);

  const handleRootChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRootCosts((prev) => ({ ...prev, [name]: value }));
  };

  const updateItem = (index: number, field: keyof InwardItem, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const stopScrollAndArrows = (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.WheelEvent<HTMLInputElement>,
  ) => {
    if ("key" in e && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
      e.preventDefault();
    }
    if ("deltaY" in e) {
      (e.target as HTMLInputElement).blur();
    }
  };

  const addItem = () => {
    const lastItem = items[items.length - 1];

    if (!lastItem.productId || !lastItem.quantity || !lastItem.totalPrice) {
      toast.error("Please complete the current item first");
      handleSummaryClick(items.length - 1);
      return;
    }

    setItems([
      ...items,
      {
        productId: "",
        productName: "",
        quantity: "",
        unit: "",
        priceType: "",
        unitPrice: "",
        totalPrice: "",
        vendorId: lastItem.vendorId,
        warehouseId: lastItem.warehouseId,
        billNumber: lastItem.billNumber,
        orderedDate: lastItem.orderedDate,
        receivedDate: lastItem.receivedDate,
        remark: "",
      },
    ]);

    setTimeout(() => {
      const newIndex = items.length;
      rowRefs.current[newIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 100);
  };

  const removeItem = (index: number) => {
    if (items.length === 1) {
      toast.error("At least one item is required");
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSummaryClick = (index: number) => {
    const rowElement = rowRefs.current[index];
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
      rowElement.classList.add("ring-2", "ring-blue-400");
      setTimeout(
        () => rowElement.classList.remove("ring-2", "ring-blue-400"),
        1500,
      );
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = () => {
    if (rootCosts.travelCost === "") {
      toast.error("Please enter Transport Cost (can be 0)");
      return;
    }
    if (rootCosts.otherCost === "") {
      toast.error("Please enter Other Cost (can be 0)");
      return;
    }

    if (editInwardId) {
      const currentData = JSON.stringify({
        items,
        travelCost: rootCosts.travelCost,
        otherCost: rootCosts.otherCost,
      });
      if (currentData === originalData) {
        toast.error("No changes detected to submit");
        return;
      }
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (
        !item.productId ||
        !item.quantity ||
        !item.unit ||
        !item.priceType ||
        !item.totalPrice ||
        !item.vendorId ||
        !item.warehouseId ||
        !item.billNumber ||
        !item.orderedDate ||
        !item.receivedDate
      ) {
        toast.error(`Row ${i + 1}: Please fill all required fields`);
        handleSummaryClick(i);
        return;
      }

      if (parseFloat(item.quantity) <= 0 || parseFloat(item.totalPrice) <= 0) {
        toast.error(`Row ${i + 1}: Quantity and Price must be greater than 0`);
        handleSummaryClick(i);
        return;
      }
    }
    setShowConfirm(true);
  };

  const resetForm = () => {
    setRootCosts({ travelCost: "", otherCost: "" });
    setItems([
      {
        productId: "",
        productName: "",
        quantity: "",
        unit: "",
        priceType: "",
        unitPrice: "",
        totalPrice: "",
        vendorId: "",
        warehouseId: warehouseAssigned || "",
        billNumber: "",
        remark: "",
        orderedDate: "",
        receivedDate: "",
      },
    ]);
  };

  const handleFinalSubmit = async () => {
    try {
      const payload = {
        travelCost: parseFloat(rootCosts.travelCost) || 0,
        otherCost: parseFloat(rootCosts.otherCost) || 0,
        storeInwardRequests: items.map((item) => ({
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          totalPrice: parseFloat(item.totalPrice),
          vendorId: item.vendorId,
          warehouseId: item.warehouseId,
          billNumber: item.billNumber,
          remark: item.remark || "",
          orderedDate: item.orderedDate || undefined,
          receivedDate: item.receivedDate || undefined,
        })),
      };

      if (editInwardId) {
        await dispatch(
          updateStoreInwardRequest({ id: editInwardId, ...payload }),
        ).unwrap();
        toast.success("Inward request updated successfully");
        onSuccess?.(); // Trigger the refresh here
      } else {
        await dispatch(createStoreInwardRequests(payload)).unwrap();
        toast.success("Inward batch created successfully");
      }

      resetForm();
      setShowConfirm(false);
      onClose();
      dispatch(
        fetchRawMaterialsByWarehousePage({
          warehouseId: items[0].warehouseId,
          page: 1,
          limit: 10,
        }),
      );
    } catch (err: any) {}
  };

  const totalMaterialCost = items.reduce(
    (sum, item) => sum + (parseFloat(item.totalPrice) || 0),
    0,
  );
  const grandTotal =
    totalMaterialCost +
    (parseFloat(rootCosts.travelCost) || 0) +
    (parseFloat(rootCosts.otherCost) || 0);

  const selectStyles = {
    control: (provided: any) => ({
      ...provided,
      padding: "0.1rem",
      fontSize: "0.875rem",
      borderRadius: "0.5rem",
      borderColor: "#CBD5E0",
      minHeight: "2.5rem",
      "&:hover": { borderColor: "#CBD5E0" },
      "&:focus": {
        borderColor: "#3B82F6",
        boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.5)",
      },
    }),
    menu: (provided: any) => ({ ...provided, zIndex: 9998 }),
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[110] animate-fade-in">
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
      `}</style>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl max-h-[95vh] flex flex-col animate-scale-in">
        <div className="flex-none bg-gray-100 px-6 py-4 flex justify-between items-center border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {editInwardId
              ? "Edit Store Inward Request"
              : "Add Store Inward Request"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3">
          <div className="lg:col-span-2 overflow-y-auto p-6 border-r border-gray-200 bg-white h-full custom-scrollbar overscroll-contain">
            <div className="bg-blue-50/50 p-4 rounded-lg mb-6 border border-blue-100">
              <h3 className="text-sm font-bold text-blue-800 mb-3 uppercase tracking-wide">
                1. Common Costs
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Transport Cost *
                  </label>
                  <input
                    type="number"
                    name="travelCost"
                    value={rootCosts.travelCost}
                    onChange={handleRootChange}
                    onKeyDown={stopScrollAndArrows}
                    onWheel={stopScrollAndArrows}
                    className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Other Cost *
                  </label>
                  <input
                    type="number"
                    name="otherCost"
                    value={rootCosts.otherCost}
                    onChange={handleRootChange}
                    onKeyDown={stopScrollAndArrows}
                    onWheel={stopScrollAndArrows}
                    className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <h3 className="text-sm font-bold text-gray-500 mb-3 uppercase tracking-wide px-1">
              2. Material Requests
            </h3>

            {items.map((item, index) => (
              <div
                key={index}
                ref={(el) => (rowRefs.current[index] = el)}
                className="bg-gray-50 p-4 rounded-lg mb-4 border border-slate-200 shadow-sm transition-all duration-300 scroll-m-20"
              >
                <div className="flex justify-between items-center mb-3 border-b border-gray-200 pb-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Item #{index + 1}
                  </span>
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700 text-xs font-medium flex items-center"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 grid grid-cols-2 gap-4">
                    <div className="col-span-1">
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Raw Material *
                      </label>
                      <AsyncPaginate
                        key={`raw-material-${index}-${materialsRefreshKey}`}
                        isDisabled={!!editInwardId}
                        loadOptions={loadRawMaterialOptions}
                        additional={{ page: 1 }}
                        debounceTimeout={300}
                        value={
                          item.productId
                            ? {
                                value: item.productId,
                                label: item.productName || "Selected",
                                unitType: item.unitType || "",
                                name: item.productName || "",
                              }
                            : null
                        }
                        onChange={(opt: RawMaterialOption | null) => {
                          const unitType = opt?.unitType || "";
                          const defaultUnit = unitType
                            ? getDefaultUnitForType(unitType)
                            : "";

                          const updated = [...items];
                          updated[index] = {
                            ...updated[index],
                            productId: opt?.value || "",
                            productName: opt?.label || "",
                            unitType,
                            unit: defaultUnit,
                          };
                          setItems(updated);
                        }}
                        placeholder="Select Material"
                        styles={selectStyles}
                        noOptionsMessage={({ inputValue }) => (
                          <div className="py-2 flex flex-col items-center gap-2">
                            <span className="text-sm text-gray-500">
                              No material found
                            </span>
                            <button
                              type="button"
                              onClick={() => openRawMaterialModal(inputValue)}
                              className="text-xs font-medium text-white bg-[#0077C0] hover:bg-[#005B90] px-3 py-1.5 rounded-md"
                            >
                              + Add New Material
                            </button>
                          </div>
                        )}
                      />
                    </div>
                    <div className="col-span-1 grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          Quantity *
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onKeyDown={stopScrollAndArrows}
                          onWheel={stopScrollAndArrows}
                          onChange={(e) => {
                            const qty = e.target.value;
                            const updated = [...items];
                            updated[index].quantity = qty;
                            if (updated[index].priceType === "unit") {
                              updated[index].totalPrice = (
                                parseFloat(qty) *
                                (parseFloat(updated[index].unitPrice) || 0)
                              ).toString();
                            }
                            setItems(updated);
                          }}
                          className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          Unit *
                        </label>
                        <Select
                          options={
                            item.productId
                              ? getUnitsByType(item.unitType || "")
                              : UNITS
                          }
                          value={
                            item.unit
                              ? UNITS.find((u) => u.value === item.unit)
                              : null
                          }
                          onChange={(opt) =>
                            updateItem(index, "unit", opt?.value || "")
                          }
                          placeholder="Unit"
                          styles={selectStyles}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Price Type *
                      </label>
                      <select
                        value={item.priceType}
                        onChange={(e) => {
                          const type = e.target.value as "unit" | "total" | "";
                          const updated = [...items];
                          updated[index] = {
                            ...updated[index],
                            priceType: type,
                            unitPrice: "",
                            totalPrice: "",
                          };
                          setItems(updated);
                        }}
                        className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-[38px]"
                      >
                        <option value="">Select Type</option>
                        <option value="unit">Price by Unit</option>
                        <option value="total">Total Price</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        {item.priceType === "unit"
                          ? "Unit Price *"
                          : "Total Price *"}{" "}
                        (Without GST)
                      </label>
                      <input
                        type="number"
                        disabled={!item.priceType}
                        onKeyDown={stopScrollAndArrows}
                        onWheel={stopScrollAndArrows}
                        value={
                          item.priceType === "unit"
                            ? item.unitPrice
                            : item.totalPrice
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          const updated = [...items];
                          if (item.priceType === "unit") {
                            updated[index].unitPrice = val;
                            updated[index].totalPrice = (
                              parseFloat(val) * (parseFloat(item.quantity) || 0)
                            ).toString();
                          } else {
                            updated[index].totalPrice = val;
                          }
                          setItems(updated);
                        }}
                        className={`w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          !item.priceType
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                        min="0"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Bill Number *
                    </label>
                    <input
                      type="text"
                      value={item.billNumber}
                      onChange={(e) =>
                        updateItem(index, "billNumber", e.target.value)
                      }
                      className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Invoice No"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Vendor *
                    </label>
                    <AsyncPaginate
                      key={`vendor-${index}-${vendorsRefreshKey}`}
                      loadOptions={loadVendorOptions}
                      additional={{ page: 1 }}
                      debounceTimeout={300}
                      value={
                        item.vendorId
                          ? {
                              value: item.vendorId,
                              label: item.vendorName || "Selected Vendor",
                            }
                          : null
                      }
                      onChange={(opt: VendorOption | null) => {
                        const updated = [...items];
                        updated[index] = {
                          ...updated[index],
                          vendorId: opt?.value || "",
                          vendorName: opt?.label || "",
                        };
                        setItems(updated);
                      }}
                      placeholder="Select Vendor"
                      styles={selectStyles}
                      noOptionsMessage={({ inputValue }) => (
                        <div className="py-2 flex flex-col items-center gap-2">
                          <span className="text-sm text-gray-500">
                            No vendor found
                          </span>
                          <button
                            type="button"
                            onClick={() => openVendorModal(inputValue)}
                            className="text-xs font-medium text-white bg-[#0077C0] hover:bg-[#005B90] px-3 py-1.5 rounded-md"
                          >
                            + Add New Vendor
                          </button>
                        </div>
                      )}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      {isWarehousePage ? "Factory *" : "Warehouse *"}
                    </label>
                    <Select
                      isDisabled={!!editInwardId}
                      options={warehouses.map((w) => ({
                        value: w._id,
                        label: w.name || "Unnamed Factory",
                      }))}
                      value={
                        item.warehouseId
                          ? {
                              value: item.warehouseId,
                              label:
                                warehouses.find(
                                  (w) => w._id === item.warehouseId,
                                )?.name || "Selected Factory",
                            }
                          : null
                      }
                      onChange={(opt) =>
                        updateItem(index, "warehouseId", opt?.value || "")
                      }
                      placeholder="Select Factory"
                      styles={selectStyles}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Ordered Date *
                      </label>
                      <input
                        type="date"
                        value={item.orderedDate}
                        onChange={(e) =>
                          updateItem(index, "orderedDate", e.target.value)
                        }
                        className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">
                        Received Date *
                      </label>
                      <input
                        type="date"
                        value={item.receivedDate}
                        onChange={(e) =>
                          updateItem(index, "receivedDate", e.target.value)
                        }
                        className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Remark
                    </label>
                    <input
                      type="text"
                      value={item.remark}
                      onChange={(e) =>
                        updateItem(index, "remark", e.target.value)
                      }
                      className="w-full p-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional remark"
                    />
                  </div>
                </div>
              </div>
            ))}

            {!editInwardId && (
              <button
                onClick={addItem}
                className="w-full p-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-[#0077C0] hover:text-[#0077C0] transition-all flex items-center justify-center gap-2 mt-4"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Another Item
              </button>
            )}
          </div>

          <div className="bg-slate-50 border-l border-gray-200 lg:col-span-1 h-full flex flex-col overflow-hidden">
            <div className="flex-none p-4 bg-slate-100 border-b border-gray-200">
              <h3 className="font-semibold text-gray-700">
                Summary ({items.length} Items)
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar overscroll-contain">
              {items.map((item, idx) => {
                const pName = item.productName
                  ? item.productName.split("(")[0]
                  : "New Item";
                const vName = item.vendorName || "-";
                const wName =
                  warehouses.find((w) => w._id === item.warehouseId)?.name ||
                  "-";

                return (
                  <div
                    key={idx}
                    onClick={() => handleSummaryClick(idx)}
                    className="bg-white p-3 rounded border border-gray-200 shadow-sm text-sm cursor-pointer hover:border-blue-400 hover:shadow-md transition-all group mb-3"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2 overflow-hidden w-3/5">
                        <span className="text-gray-400 text-xs font-mono">
                          #{idx + 1}
                        </span>
                        <span
                          className="font-bold text-blue-700 truncate"
                          title={pName}
                        >
                          {pName}
                        </span>
                      </div>
                      <div className="flex items-center justify-end gap-3 w-2/5">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                          {item.quantity || "0"} {item.unit}
                        </span>
                        <span className="font-bold text-slate-800">
                          {parseFloat(item.totalPrice || "0").toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-500 mb-2 border-t border-dashed border-gray-100 pt-2">
                      <div className="truncate">
                        <span className="block text-[9px] text-gray-400 uppercase">
                          Bill No
                        </span>
                        <span
                          className="font-medium text-slate-700"
                          title={item.billNumber}
                        >
                          {item.billNumber || "-"}
                        </span>
                      </div>
                      <div className="truncate text-center">
                        <span className="block text-[9px] text-gray-400 uppercase">
                          Vendor
                        </span>
                        <span
                          className="font-medium text-slate-700"
                          title={vName}
                        >
                          {vName}
                        </span>
                      </div>
                      <div className="truncate text-right">
                        <span className="block text-[9px] text-gray-400 uppercase">
                          Factory
                        </span>
                        <span
                          className="font-medium text-slate-700"
                          title={wName}
                        >
                          {wName}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-[10px] text-gray-500 bg-slate-50 px-2 py-1 rounded">
                      <div>
                        <span className="text-gray-400 mr-1">Ord:</span>
                        <span className="font-medium text-slate-600">
                          {formatDate(item.orderedDate)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400 mr-1">Rec:</span>
                        <span className="font-medium text-slate-600">
                          {formatDate(item.receivedDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex-none p-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
              <div className="space-y-1 text-sm mb-3">
                <div className="flex justify-between text-gray-500">
                  <span>Materials:</span>
                  <span>{totalMaterialCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Transport:</span>
                  <span>
                    {(parseFloat(rootCosts.travelCost) || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-500">
                  <span>Other:</span>
                  <span>
                    {(parseFloat(rootCosts.otherCost) || 0).toFixed(2)}
                  </span>
                </div>
                <div className="h-px bg-gray-200 my-2"></div>
                <div className="flex justify-between font-bold text-gray-800 text-lg">
                  <span>Grand Total:</span>
                  <span>{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-none flex gap-3 p-6 border-t border-slate-200 bg-white z-10">
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 bg-[#0077C0] text-white py-3 rounded-lg hover:bg-[#0069AA] focus:ring-2 focus:ring-[#0069AA] focus:ring-offset-2 transition-all font-medium flex items-center justify-center gap-2"
          >
            {loading ? (
              <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : editInwardId ? (
              "Update Inward Entry"
            ) : (
              "Submit Batch Entry"
            )}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-white text-slate-700 py-3 rounded-lg border border-slate-300 hover:bg-gray-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
      {/* CONFIRMATION OVERLAY */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60] animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-8 animate-scale-in">
            <div className="mb-6 border-b pb-3">
              <h3 className="text-xl font-bold text-slate-800">
                Verify Material Entries
              </h3>
              <p className="text-sm text-slate-500">
                Please double-check each item before final submission.
              </p>
            </div>

            <div className="flex px-4 py-2 bg-slate-50 border border-slate-200 rounded text-xs font-bold text-slate-600 uppercase mb-2">
              <div className="flex-[3]">Material Name</div>
              <div className="flex-1 text-center">Quantity</div>
              <div className="flex-1 text-right">Total Price</div>
            </div>

            <div className="space-y-1 mb-8 max-h-[50vh] overflow-y-auto custom-scrollbar border border-slate-100 rounded-lg">
              {items.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center px-4 py-4 border-b border-slate-50 hover:bg-blue-50/30 transition-colors"
                >
                  <div className="flex-[3] font-medium text-slate-900 truncate pr-4">
                    {item.productName?.split("(")[0]}
                  </div>
                  <div className="flex-1 text-center font-bold text-slate-800">
                    {item.quantity} {item.unit}
                  </div>
                  <div className="flex-1 text-right font-bold text-blue-600">
                    ₹
                    {parseFloat(item.totalPrice).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center py-4 px-4 bg-slate-50 border border-slate-200 rounded-lg mb-8">
              <span className="text-sm font-bold text-slate-600 uppercase tracking-wider">
                Batch Grand Total
              </span>
              <span className="text-2xl font-bold text-slate-900">
                ₹
                {grandTotal.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleFinalSubmit}
                disabled={loading}
                className="flex-[2] bg-[#0077C0] text-white py-3.5 rounded-lg font-bold text-lg hover:bg-[#0069AA] transition-all"
              >
                {loading
                  ? "Processing..."
                  : editInwardId
                    ? "Confirm and Update Batch"
                    : "Confirm and Submit Batch"}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 bg-white text-slate-600 py-3.5 rounded-lg border border-slate-300 font-bold hover:bg-slate-50 transition-all"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}
      <RawMaterialModal
        isOpen={isRawMaterialModalOpen}
        onClose={() => setIsRawMaterialModalOpen(false)}
        editMode={false}
        selectedItem={null}
        rawMaterialForm={rawMaterialForm}
        setRawMaterialForm={setRawMaterialForm}
        rawMaterialPage={1}
        itemsPerPage={10}
        searchQuery=""
        getRawMaterials={getRawMaterials}
      />
      <VendorModal
        isOpen={isVendorModalOpen}
        onClose={() => {
          setIsVendorModalOpen(false);
          setVendorsRefreshKey((key) => key + 1);
        }}
        editMode={false}
        selectedItem={null}
        vendorForm={vendorForm}
        setVendorForm={setVendorForm}
        vendorPage={1}
        itemsPerPage={10}
        searchQuery=""
      />
    </div>
  );
};

export default AddStoreInwardModal;
